import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { getFormEntriesByFormName } from "../services/formServices";
import toast from "react-hot-toast";
import { FORMNAMES } from "../utilities/codes";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { useNavigate } from "react-router-dom";
import { mediaSource } from "../utilities/mediautilities";
import { FaArrowRight, FaPercentage } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";

const TopperStudent = () => {
    const [selectedStudent, setSelectedStudent] = useState(null);
    const { schoolDetails, topperStudents, setTopperStudents } = useContext(schoolContext);
    const { t } = useTranslation()

    const navigate = useNavigate()

    useEffect(() => {
        const getTopperStudents = async () => {
            try {
                const response = await getFormEntriesByFormName(FORMNAMES.TOPPER_STUDENTS);
                if (response?.length) {
                    setTopperStudents(response);
                }
            } catch (error) {
                toast.error(error.message);
            }
        };

        getTopperStudents();
    }, [setTopperStudents]);


    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15 }
        }
    };

    return (
        <>
            {topperStudents?.length > 0 &&
                <>
                    <section
                        id="topper-students"
                        className="py-20 px-6 relative bg-gray-100"
                    >
                        <div className="relative z-10 max-w-7xl mx-auto mb-8">
                            {/* Heading */}
                            <motion.h2
                                initial={{ opacity: 0, y: -30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className="text-4xl md:text-5xl font-bold text-center text-primary mb-4"
                            >
                                {t(I18N_KEYS.TOPSTUDENTTITLE)}
                            </motion.h2>

                            {schoolDetails?.student_subtitle &&
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.3 }}
                                    className="text-gray-600 mt-3 mb-14 text-center text-lg max-w-2xl mx-auto"
                                >
                                    {schoolDetails?.student_subtitle}
                                </motion.p>
                            }

                            {/* Cards */}
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                className="common-container grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
                            >
                                {topperStudents?.slice(0, 6)?.map((student, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        className="h-full"
                                        onClick={() => setSelectedStudent(student)}
                                    >
                                        {/* Main Card: Removed hover shadow and lift classes */}
                                        <div className="bg-white rounded-2xl shadow-sm hover:shadow-md h-full flex flex-col border border-gray-200 p-6 items-center text-center cursor-pointer">

                                            {/* Profile Image Section */}
                                            <div className="relative mb-4">
                                                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-50">
                                                    <img
                                                        src={mediaSource(student?.payload?.name_ref?.profile_photo?.fileUrl)}
                                                        alt={student?.payload?.name_ref?.student_name || "Student"}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => mediaSource(e)}
                                                    />
                                                </div>
                                            </div>

                                            {/* Content Section */}
                                            <div className="flex-1 flex flex-col w-full">
                                                <h3 className="text-xl text-gray-900 mb-1">
                                                    {student?.payload?.full_name || student?.payload?.name_ref?.full_name}
                                                </h3>

                                                <p className="text-sm text-gray-500 mb-4">
                                                    {t(I18N_KEYS.CLASS)} {student?.payload?.name_ref?.class_ref?.class}
                                                </p>

                                                {/* Percentage Box */}
                                                {student?.payload?.percentage && (
                                                    <div className="rounded-lg py-2 px-4 mb-4 inline-block mx-auto">
                                                        <span className="text-2xl font-black text-primary flex items-center justify-center gap-1 text-md">
                                                            {student?.payload?.percentage}<FaPercentage className="text-sm" />
                                                        </span>
                                                        <p className="text-[10px] uppercase tracking-widest text-gray-400">
                                                            {t(I18N_KEYS.PERCENTAGE)}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Achievement Text */}
                                                {student?.payload?.achievement && (
                                                    <div className="mb-2 px-2">
                                                        <p className="text-sm text-gray-600 italic">
                                                            "{student?.payload?.achievement}"
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Connection-style Button */}
                                                <div className="mt-auto flex justify-center">
                                                    <span className="inline-flex items-center gap-2 px-8 py-2 rounded-full border-2 border-primary text-primary text-sm">
                                                        {t(I18N_KEYS.VIEWPROFILE)}
                                                        <FaArrowRight className="text-xs" />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                        {topperStudents?.length > 6 &&
                            <motion.div
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                className="text-center"
                            >
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => navigate('/topstudents')}
                                    className="px-8 py-3 bg-primary text-accent rounded-lg font-semibold text-sm md:text-base hover:bg-primary/90 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2"
                                >
                                    <span>{t(I18N_KEYS.VIEWSTUDENTBUTTON)}</span>
                                    <span><FaArrowRight /></span>
                                </motion.button>
                            </motion.div>
                        }
                    </section>

                    {/* Modal for detailed view */}
                    <AnimatePresence>
                        {selectedStudent && (
                            <motion.div
                                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setSelectedStudent(null)}
                            >
                                <button
                                    onClick={() => setSelectedStudent(null)}
                                    className="absolute top-4 right-4 text-accent text-3xl font-bold hover:text-primary z-10"
                                >
                                    &times;
                                </button>
                                <motion.div
                                    className="bg-accent rounded-2xl overflow-hidden shadow-2xl max-w-2xl w-full"
                                    initial={{ scale: 0.8, y: 40 }}
                                    animate={{ scale: 1, y: 0 }}
                                    exit={{ scale: 0.8, y: 40 }}
                                    transition={{ duration: 0.3 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="p-8">
                                        {/* Student Photo */}
                                        <div className="h-48 w-48 mx-auto rounded-full overflow-hidden mb-6 border-4 border-primary shadow-lg">
                                            <img
                                                src={mediaSource(selectedStudent?.payload?.name_ref?.profile_photo?.fileUrl)}
                                                alt={selectedStudent?.payload?.name_ref?.full_name || "Topper Student"}
                                                className="h-full w-full object-cover"
                                                onError={(e) => mediaSource(e)}
                                            />
                                        </div>

                                        {/* Student Details */}
                                        <div className="text-center">
                                            {selectedStudent?.payload?.name_ref?.full_name &&
                                                <h3 className="text-3xl font-bold mb-3 text-gray-800">
                                                    {selectedStudent?.payload?.name_ref?.full_name}
                                                </h3>
                                            }
                                            {selectedStudent?.payload?.name_ref?.class_ref?.class &&
                                                <p className="text-primary font-semibold text-lg mb-4">
                                                    {t(I18N_KEYS.CLASS)} {selectedStudent?.payload?.name_ref?.class_ref?.class}
                                                </p>
                                            }
                                            {selectedStudent?.payload?.percentage &&
                                                <div className="mb-6 flex flex-col items-center">
                                                    <span className="text-4xl font-black text-secondary flex items-center">
                                                        {selectedStudent?.payload?.percentage}<FaPercentage />
                                                    </span>
                                                    <p className="text-gray-600 mt-2">{t(I18N_KEYS.OVERALLPER)}</p>
                                                </div>
                                            }
                                            {selectedStudent?.payload?.achievement &&
                                                <div className="bg-primary/10 rounded-lg p-4 mb-4">
                                                    <h4 className="font-semibold text-primary mb-2">{t(I18N_KEYS.ACHIEVEMENT)}</h4>
                                                    <p className="text-gray-700 leading-relaxed"
                                                        dangerouslySetInnerHTML={{
                                                            __html: selectedStudent?.payload?.achievement
                                                        }}
                                                    >
                                                    </p>
                                                </div>
                                            }
                                            {selectedStudent?.payload?.description &&
                                                <p className="text-gray-600 leading-relaxed"
                                                    dangerouslySetInnerHTML={{
                                                        __html: selectedStudent?.payload?.description
                                                    }}
                                                >
                                                </p>
                                            }
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            }
        </>
    );
}

export default TopperStudent;
